import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { GoogleLogo, CheckCircle, XCircle } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Card to connect/disconnect the current user's Google account.
 * Admins get Calendar + Gmail scopes ; employees get Calendar-only.
 */
export const GoogleAccountCard = ({ isAdmin = false }) => {
  const [status, setStatus] = useState({ connected: false, google_email: '', connected_at: '' });
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/oauth/google/status`, { withCredentials: true });
      setStatus(r.data);
    } catch { /* silent — keep default not connected */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Pick up callback redirect (?google_connected=1 / ?google_error=)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_connected') === '1') {
      toast.success('Compte Google connecte');
      window.history.replaceState({}, document.title, window.location.pathname);
      fetchStatus();
    } else if (params.get('google_error')) {
      toast.error(`Erreur Google: ${params.get('google_error')}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [fetchStatus]);

  const connect = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/oauth/google/authorize`, { withCredentials: true });
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur connexion Google');
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Deconnecter votre compte Google ?')) return;
    try {
      await axios.delete(`${API_URL}/api/oauth/google/disconnect`, { withCredentials: true });
      toast.success('Compte Google deconnecte');
      setStatus({ connected: false, google_email: '', connected_at: '' });
    } catch {
      toast.error('Erreur deconnexion');
    }
  };

  return (
    <div className="card" data-testid="google-account-card">
      <div className="card-body" style={{ padding: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <GoogleLogo size={22} weight="bold" color="#34A853" />
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Compte Google</h3>
        </div>
        <p style={{ margin: '0 0 14px 0', fontSize: '0.85rem', color: '#5E5F66' }}>
          {isAdmin
            ? 'Synchronisez votre Google Agenda et accedez a votre messagerie Gmail directement depuis BATI-BOYS.'
            : 'Synchronisez vos taches BATI-BOYS sur votre Google Agenda personnel.'}
        </p>

        {loading ? (
          <div style={{ fontSize: '0.85rem', color: '#9CA3AF' }}>Chargement...</div>
        ) : status.connected ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 8, backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0', flex: 1, minWidth: 200 }}>
              <CheckCircle size={20} weight="fill" color="#16A34A" />
              <div>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#166534' }}>Connecte</div>
                <div style={{ fontSize: '0.78rem', color: '#15803D' }} data-testid="google-connected-email">{status.google_email || ''}</div>
              </div>
            </div>
            <button data-testid="google-disconnect-settings-btn" className="btn btn-secondary" onClick={disconnect} style={{ color: '#C62828', borderColor: '#FCA5A5' }}>
              <XCircle size={14} /> Deconnecter
            </button>
          </div>
        ) : (
          <button data-testid="google-connect-settings-btn" className="btn btn-primary" onClick={connect} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <GoogleLogo size={16} weight="bold" /> Connecter mon compte Google
          </button>
        )}

        {!isAdmin && status.connected && (
          <div style={{ marginTop: 12, padding: '8px 12px', backgroundColor: '#EFF6FF', borderRadius: 6, fontSize: '0.78rem', color: '#1E40AF' }}>
            Vos taches creees dans BATI-BOYS apparaitront automatiquement sur votre Google Agenda.
          </div>
        )}
      </div>
    </div>
  );
};
